import React, { useEffect } from 'react';

/**
 * Gamification2026 Modal
 *
 * Responsive behavior (matches Figma spec):
 *   - Mobile  (< 740px): full-screen overlay — no backdrop, dialog fills the viewport.
 *   - Tablet+ (≥ 740px): centered floating card over a dimmed backdrop.
 *
 * Props (fully backward-compatible with src/components/shared/Modal.js):
 *   show        {boolean}  — controls visibility
 *   untoggle    {Function} — called when backdrop is clicked or Escape pressed
 *   extraSmall  {boolean}  — max-width 400px on tablet+
 *   small       {boolean}  — max-width 560px on tablet+
 *   large       {boolean}  — max-width 720px on tablet+  (default: 480px)
 *   lessPadding {boolean}  — reduced inner padding
 *   transparent {boolean}  — transparent content area (no white bg)
 *   children    {node}
 */
const Modal = ({
  show,
  untoggle,
  extraSmall,
  small,
  large,
  lessPadding,
  transparent,
  children,
  className
}) => {
  // Lock body scroll while open; restore on close/unmount
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  // Close on Escape key
  useEffect(() => {
    if (!show || !untoggle) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') untoggle();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [show, untoggle]);

  if (!show) return null;

  const dialogClass = [
    'gl2026-modal__dialog',
    className,
    extraSmall && 'gl2026-modal__dialog--extra-small',
    small      && 'gl2026-modal__dialog--small',
    large      && 'gl2026-modal__dialog--large',
  ].filter(Boolean).join(' ');

  const contentClass = [
    'gl2026-modal__content',
    lessPadding  && 'gl2026-modal__content--less-padding',
    transparent  && 'gl2026-modal__content--transparent',
  ].filter(Boolean).join(' ');

  return (
    <>
      {/* Backdrop — visible only on tablet+ (CSS handles the hide on mobile) */}
      <div
        className="gl2026-modal__backdrop"
        onClick={untoggle}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className={dialogClass}
        role="dialog"
        aria-modal="true"
      >
        <div className={contentClass}>
          {children}
        </div>
      </div>
    </>
  );
};

export default Modal;
